import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { 
  Users, Globe, Lightbulb, Award, Target, 
  Heart, Check, ArrowRight, Quote, Star
} from "lucide-react";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Founder Image
const FOUNDER_IMAGE = "/assets/images/founder.jpg";

const AboutPage = () => {
  const coreValues = [
    { icon: Award, title: "Leadership Excellence", desc: "Developing transformative leaders" },
    { icon: Lightbulb, title: "Innovation", desc: "Embracing new ideas and approaches" },
    { icon: Users, title: "Collaboration", desc: "Working together for impact" },
    { icon: Heart, title: "Integrity", desc: "Upholding ethical standards" },
    { icon: Target, title: "Impact", desc: "Creating meaningful change" },
  ];

  const whyGSLC = [
    { icon: Target, title: "Focused Audience", desc: "Only decision-makers and senior leadership from schools" },
    { icon: Star, title: "High-Quality Curation", desc: "Thoughtfully designed agendas and speaker line-ups" },
    { icon: Globe, title: "Pan-India Presence", desc: "Events across multiple cities" },
    { icon: Heart, title: "Long-Term Vision", desc: "Building a strong leadership community, not just one-time events" },
  ];

  const whoShouldJoin = [
    "School Owners, Founders & Directors",
    "Principals, Vice Principals & Academic Heads",
    "Policy & Assessment Bodies"
  ];

  const communityBenefits = [
    "Access to premium leadership forums",
    "Networking with like-minded decision makers",
    "Early access to GSLC events & initiatives",
    "Opportunities to speak, collaborate, and showcase innovations"
  ];

  return (
    <div className="pt-24">
      {/* Founder's Corner - AT THE TOP with VERY BIG Image and FULL Message */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-blue-50 via-white to-yellow-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">From The Founder</span>
              <h1 className="font-heading text-4xl md:text-6xl font-semibold mt-4 text-slate-800">
                About <span className="gradient-text">GSLC</span>
              </h1>
            </div>

            <div className="card-light rounded-3xl p-8 md:p-12">
              {/* Very Big Image on Top */}
              <div className="flex justify-center mb-10">
                <img 
                  src={FOUNDER_IMAGE} 
                  alt="Pankaj Sharma - Founder GSLC"
                  className="w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 rounded-3xl object-cover shadow-2xl border-4 border-white"
                />
              </div>

              <div className="text-center mb-8">
                <h2 className="font-heading text-3xl text-slate-800">Pankaj Sharma</h2>
                <p className="text-red-600 text-lg">Founder & CEO, GSLC</p>
              </div>
              
              <Quote className="w-14 h-14 text-yellow-300 mx-auto mb-6" />
              
              {/* FULL Founder's Message */}
              <div className="space-y-6 max-w-4xl mx-auto">
                <p className="text-slate-700 text-lg leading-relaxed">
                  Dear School Leaders,
                </p>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Education today stands at a defining crossroads. The world around us is changing faster than ever before, and with it, the role of schools and school leaders is evolving. Leadership is no longer just about managing institutions — it is about shaping mindsets, building futures, and creating impact that lasts generations.
                </p>
                <p className="text-slate-600 text-lg leading-relaxed">
                  At GSLC, we strongly believe that great schools are built by visionary leaders — leaders who are courageous enough to innovate, compassionate enough to put students first, and committed enough to continuously evolve.
                </p>
                <p className="text-slate-600 text-lg leading-relaxed">
                  GSLC was created with a simple yet powerful purpose: to give school leaders a platform where ideas meet action, where experience meets innovation, and where challenges turn into opportunities.
                </p>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Through GSLC, we aim to build a strong community of progressive educators who learn from each other, grow together, and collectively raise the standards of education in our country.
                </p>
                <p className="text-slate-600 text-lg leading-relaxed">
                  I invite you to join this journey — not just to adapt to the future of education, but to lead it.
                </p>
                <div className="pt-6 text-right">
                  <p className="text-slate-600 text-lg">Warm regards,</p>
                  <p className="text-slate-800 font-heading text-xl font-semibold">Pankaj Sharma</p>
                  <p className="text-red-600">Founder, GSLC</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Full About Section */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto">
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              The Global School Leaders Consortium (GSLC) is a collaborative platform created to empower school leaders 
              and education stakeholders across India. We curate high-impact conferences, leadership conclaves, roundtables, 
              and knowledge forums where decision-makers from K-12 education come together to exchange ideas, discover 
              innovations, and build meaningful partnerships.
            </p>
            <div className="bg-gradient-to-r from-yellow-50 to-red-50 rounded-2xl p-8 border-l-4 border-red-600">
              <p className="text-xl text-slate-800 font-heading italic">
                GSLC is not just an event company — it is a movement to strengthen school leadership and improve learning outcomes at scale.
              </p>
            </div>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Why GSLC Matters */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-blue-50 via-white to-yellow-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-5xl font-medium text-slate-800">
              Why GSLC <span className="gradient-text">Matters</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {whyGSLC.map((item, index) => (
              <motion.div key={index} variants={fadeInUp} className="card-light rounded-2xl p-6 text-center hover-lift">
                <item.icon className="w-10 h-10 mx-auto mb-4 text-red-600" />
                <h4 className="font-heading text-lg text-slate-800 mb-2">{item.title}</h4>
                <p className="text-slate-500 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Who Should Join & Community Benefits */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Who Should Join */}
            <motion.div variants={fadeInUp} className="card-light rounded-2xl p-8">
              <h3 className="font-heading text-2xl text-slate-800 mb-6">Who Should Join GSLC</h3>
              <ul className="space-y-4">
                {whoShouldJoin.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-600">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-100 to-red-100 flex items-center justify-center">
                      <Users className="w-5 h-5 text-red-600" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* What Our Community Gets */}
            <motion.div variants={fadeInUp} className="card-light rounded-2xl p-8">
              <h3 className="font-heading text-2xl text-slate-800 mb-6">What Our Community Gets</h3>
              <ul className="space-y-4">
                {communityBenefits.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-600">
                    <Check className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      {/* Core Values */}
      <AnimatedSection className="section-padding bg-gradient-to-r from-slate-50 via-white to-slate-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-slate-800">
              Core <span className="gradient-text">Values</span>
            </h2>
          </motion.div>

          <motion.div variants={fadeInUp} className="grid md:grid-cols-5 gap-6">
            {coreValues.map((value, index) => (
              <div key={index} className="card-light rounded-2xl p-6 text-center hover-lift card-gradient-hover">
                <value.icon className="w-10 h-10 mx-auto mb-4 text-red-600" />
                <h4 className="font-heading text-lg text-slate-800 mb-2">{value.title}</h4>
                <p className="text-slate-500 text-sm">{value.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Our Network */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-blue-50 via-white to-yellow-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-slate-800 mb-4">
              Our Network
            </h2>
            <p className="font-heading text-2xl md:text-3xl text-red-600 italic">
              “One Network. Many Voices. One Future for Education.”
            </p>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* CTA */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          <motion.div 
            variants={fadeInUp}
            className="bg-gradient-to-r from-yellow-500 via-red-600 to-blue-800 rounded-3xl p-12 text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
              Be Part of the Movement
            </h2>
            <p className="text-white/90 mb-8 max-w-xl mx-auto">
              Connect with visionary leaders, exchange transformative ideas, and shape the future of education together.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-white text-blue-900 font-semibold hover:bg-yellow-400 transition-colors">
              Get In Touch <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default AboutPage;
